/**
 * Admin-only version scoreboard for a surfacer family:
 * `GET /api/reader/cohort/<cohort>/surfacer/scoreboard` (web-demo-backend #792).
 *
 * `cohort` is any version cohort of the family or the family's predictions
 * source cohort. Every report-matching version is scored against the family's
 * pooled verdicts. The field definitions live with the backend
 * (`src/surfacer_scoreboard.py` `score_version`); the page restates them.
 */

import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";

/** One version's counts. Every count is over (study, finding) pairs. */
export interface ScoreboardVersion {
  cohort_id: string;
  models: string[];
  studies: number;
  candidates_shown: number;
  surfaced: number;
  hidden_reported: number;
  sunk_no_coverage: number;
  true_miss_kept: number;
  true_miss_kept_billable: number;
  true_miss_hidden: number;
  true_miss_sunk_no_coverage: number;
  report_mentions_hidden: number;
  report_mentions_shown: number;
  /** Labelled `other_report` (the report sends the region to a separate report),
   * by where the version put it. Absent from a backend that predates the verdict. */
  other_report_shown?: number;
  other_report_hidden?: number;
  other_report_sunk_no_coverage?: number;
  overcall_shown: number;
  wrong_name_shown: number;
  inconsequential_shown: number;
  ambiguous: number;
  unjudged_shown: number;
  unjudged_hidden: number;
}

export interface SurfacerScoreboard {
  requested_cohort_id: string;
  /** Null when the family can't be resolved; it is then the requested cohort alone. */
  source_cohort_id: string | null;
  /** Family members with no comparison rows, e.g. the predictions cohort itself. */
  non_version_cohort_ids: string[];
  pooled: {
    judged_pairs: number;
    conflicting_pairs: number;
    reviewers: number;
  };
  versions: ScoreboardVersion[];
}

/** Thrown for a 404: no family is known for that cohort id. */
export class ScoreboardNotFoundError extends Error {
  constructor(cohortId: string) {
    super(`No surfacer versions found for cohort ${cohortId}`);
    this.name = "ScoreboardNotFoundError";
  }
}

async function fetchScoreboard(cohortId: string): Promise<SurfacerScoreboard> {
  try {
    const response = await axios.get<SurfacerScoreboard>(
      `${getApiBaseUrl()}/api/reader/cohort/${encodeURIComponent(cohortId)}/surfacer/scoreboard`,
      { withCredentials: true },
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new ScoreboardNotFoundError(cohortId);
    }
    throw error;
  }
}

export function useSurfacerScoreboard(cohortId: string | undefined) {
  return useQuery({
    queryKey: ["surfacer-scoreboard", cohortId ?? ""],
    queryFn: () => fetchScoreboard(cohortId!),
    enabled: !!cohortId,
    // Scores move only as verdicts land; a minute is fresh enough and the
    // backend scans every verdict to build it.
    staleTime: 60 * 1000,
    retry: (failureCount, error) => !(error instanceof ScoreboardNotFoundError) && failureCount < 1,
  });
}
